import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const AISettingsContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-gray-800);
  }
  
  p {
    margin: 0.5rem 0 0 0;
    color: var(--color-gray-600);
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  gap: 2rem;
`;

const SettingCard = styled.div`
  background: var(--color-white);
  padding: 2rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  
  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-gray-800);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  p {
    margin: 0 0 1.5rem 0;
    color: var(--color-gray-600);
    line-height: 1.5;
  }
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingLabel = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    font-weight: 500;
    color: var(--color-gray-800);
  }
  
  span {
    font-size: 0.875rem;
    color: var(--color-gray-600);
  }
`;

const SettingControl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 24px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background-color: var(--color-primary);
  }
  
  input:checked + span:before {
    transform: translateX(26px);
  }
`;

const Slider = styled.input`
  width: 200px;
  height: 6px;
  border-radius: 3px;
  background: #d3d3d3;
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
  }
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  background: var(--color-white);
  font-size: 0.875rem;
  min-width: 150px;
`;

const Input = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  background: var(--color-white);
  font-size: 0.875rem;
  width: 150px;
`;

const StatusBadge = styled.span<{ $status: 'online' | 'offline' | 'error' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => 
    props.$status === 'online' ? '#D1FAE5' :
    props.$status === 'offline' ? '#FEE2E2' :
    '#FEF3C7'
  };
  color: ${props => 
    props.$status === 'online' ? '#065F46' :
    props.$status === 'offline' ? '#991B1B' :
    '#92400E'
  };
`;

const SaveButton = styled.button`
  padding: 0.75rem 2rem;
  background: var(--color-primary);
  color: var(--color-white);
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--color-primary-dark);
  }
`;

const TestButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--color-white);
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-primary);
    color: var(--color-white);
  }
`;

const AISettings: React.FC = () => {
  const [settings, setSettings] = useState({
    contentModeration: {
      enabled: true,
      sensitivity: 75,
      autoApprove: true,
      autoReject: false,
    },
    moderationThresholds: {
      confidenceThreshold: 0.8,
      safeConfidence: 0.9,
      unsafeConfidence: 0.8,
      contentLimit: 500,
    },
    storyAnalysis: {
      enabled: true,
      checkAppropriate: true,
      languageFilter: true,
      topicAnalysis: true,
    },
    performance: {
      responseTime: 2.3,
      accuracy: 94.2,
      uptime: 99.8,
    }
  });

  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Load current moderation settings from API
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch('/api/admin/ai-settings/moderation/current');
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({
            ...prev,
            moderationThresholds: data
          }));
        }
      } catch (error) {
        console.error('Failed to load AI settings:', error);
      }
    };
    loadSettings();
  }, []);

  const handleSettingChange = (section: string, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [key]: value
      }
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setSaveMessage('');
    try {
      // Get the default INAPPROPRIATE_DETECTION configuration
      const configResponse = await fetch('/api/admin/ai-settings/configurations/default/INAPPROPRIATE_DETECTION');
      if (!configResponse.ok) {
        throw new Error('Could not find AI configuration');
      }
      const config = await configResponse.json();

      // Update moderation settings
      const response = await fetch(`/api/admin/ai-settings/configurations/${config.id}/moderation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings.moderationThresholds)
      });

      if (response.ok) {
        setSaveMessage('✅ Settings saved successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save AI settings:', error);
      setSaveMessage('❌ Failed to save settings');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = () => {
    console.log('Testing AI connection...');
    // TODO: Implement AI connection test
  };

  return (
    <AISettingsContainer>
      <PageHeader>
        <h1>🤖 AI Configuration</h1>
        <p>Configure AI moderation and analysis settings for content review</p>
      </PageHeader>

      <SettingsGrid>
        {/* AI Service Status */}
        <SettingCard>
          <h3>🔌 AI Service Status</h3>
          <p>Current status and connection information for the AI service</p>
          
          <SettingRow>
            <SettingLabel>
              <h4>Service Status</h4>
              <span>Current operational status</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status="online">Online</StatusBadge>
              <TestButton onClick={handleTestConnection}>Test Connection</TestButton>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>API Endpoint</h4>
              <span>Ollama service endpoint</span>
            </SettingLabel>
            <SettingControl>
              <Input defaultValue="http://ollama:11434" />
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Model</h4>
              <span>Active AI model for content analysis</span>
            </SettingLabel>
            <SettingControl>
              <Select defaultValue="llama2">
                <option value="llama2">Llama 2</option>
                <option value="mistral">Mistral</option>
                <option value="codellama">Code Llama</option>
              </Select>
            </SettingControl>
          </SettingRow>
        </SettingCard>

        {/* Content Moderation */}
        <SettingCard>
          <h3>🛡️ Content Moderation</h3>
          <p>Configure automatic content moderation and filtering settings</p>
          
          <SettingRow>
            <SettingLabel>
              <h4>Enable Content Moderation</h4>
              <span>Automatically analyze all new content</span>
            </SettingLabel>
            <SettingControl>
              <Toggle>
                <input 
                  type="checkbox" 
                  checked={settings.contentModeration.enabled}
                  onChange={(e) => handleSettingChange('contentModeration', 'enabled', e.target.checked)}
                />
                <span></span>
              </Toggle>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Sensitivity Level</h4>
              <span>How strict the content filtering should be</span>
            </SettingLabel>
            <SettingControl>
              <Slider
                type="range"
                min="0"
                max="100"
                value={settings.contentModeration.sensitivity}
                onChange={(e) => handleSettingChange('contentModeration', 'sensitivity', parseInt(e.target.value))}
              />
              <span>{settings.contentModeration.sensitivity}%</span>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Auto-Approve Safe Content</h4>
              <span>Automatically approve content marked as safe</span>
            </SettingLabel>
            <SettingControl>
              <Toggle>
                <input 
                  type="checkbox" 
                  checked={settings.contentModeration.autoApprove}
                  onChange={(e) => handleSettingChange('contentModeration', 'autoApprove', e.target.checked)}
                />
                <span></span>
              </Toggle>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Auto-Reject Inappropriate Content</h4>
              <span>Automatically reject content flagged as inappropriate</span>
            </SettingLabel>
            <SettingControl>
              <Toggle>
                <input 
                  type="checkbox" 
                  checked={settings.contentModeration.autoReject}
                  onChange={(e) => handleSettingChange('contentModeration', 'autoReject', e.target.checked)}
                />
                <span></span>
              </Toggle>
            </SettingControl>
          </SettingRow>
        </SettingCard>

        {/* Moderation Thresholds */}
        <SettingCard>
          <h3>⚖️ Moderation Thresholds</h3>
          <p>Configure AI confidence thresholds and content analysis limits</p>

          <SettingRow>
            <SettingLabel>
              <h4>Confidence Threshold</h4>
              <span>Minimum confidence for high-confidence decisions (0.0 - 1.0)</span>
            </SettingLabel>
            <SettingControl>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={settings.moderationThresholds.confidenceThreshold}
                onChange={(e) => handleSettingChange('moderationThresholds', 'confidenceThreshold', parseFloat(e.target.value))}
              />
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Safe Content Confidence</h4>
              <span>Confidence score when AI determines content is SAFE (0.0 - 1.0)</span>
            </SettingLabel>
            <SettingControl>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={settings.moderationThresholds.safeConfidence}
                onChange={(e) => handleSettingChange('moderationThresholds', 'safeConfidence', parseFloat(e.target.value))}
              />
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Unsafe Content Confidence</h4>
              <span>Confidence score when AI determines content is UNSAFE (0.0 - 1.0)</span>
            </SettingLabel>
            <SettingControl>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={settings.moderationThresholds.unsafeConfidence}
                onChange={(e) => handleSettingChange('moderationThresholds', 'unsafeConfidence', parseFloat(e.target.value))}
              />
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Content Analysis Limit</h4>
              <span>Maximum characters to analyze (100 - 10000)</span>
            </SettingLabel>
            <SettingControl>
              <Input
                type="number"
                min="100"
                max="10000"
                step="100"
                value={settings.moderationThresholds.contentLimit}
                onChange={(e) => handleSettingChange('moderationThresholds', 'contentLimit', parseInt(e.target.value))}
              />
              <span>{settings.moderationThresholds.contentLimit} chars</span>
            </SettingControl>
          </SettingRow>
        </SettingCard>

        {/* Story Analysis */}
        <SettingCard>
          <h3>📖 Story Analysis</h3>
          <p>Configure AI analysis features for story content</p>
          
          <SettingRow>
            <SettingLabel>
              <h4>Enable Story Analysis</h4>
              <span>Analyze stories for content appropriateness</span>
            </SettingLabel>
            <SettingControl>
              <Toggle>
                <input 
                  type="checkbox" 
                  checked={settings.storyAnalysis.enabled}
                  onChange={(e) => handleSettingChange('storyAnalysis', 'enabled', e.target.checked)}
                />
                <span></span>
              </Toggle>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Age Appropriateness Check</h4>
              <span>Verify content is suitable for children</span>
            </SettingLabel>
            <SettingControl>
              <Toggle>
                <input 
                  type="checkbox" 
                  checked={settings.storyAnalysis.checkAppropriate}
                  onChange={(e) => handleSettingChange('storyAnalysis', 'checkAppropriate', e.target.checked)}
                />
                <span></span>
              </Toggle>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Language Filter</h4>
              <span>Filter inappropriate language and profanity</span>
            </SettingLabel>
            <SettingControl>
              <Toggle>
                <input 
                  type="checkbox" 
                  checked={settings.storyAnalysis.languageFilter}
                  onChange={(e) => handleSettingChange('storyAnalysis', 'languageFilter', e.target.checked)}
                />
                <span></span>
              </Toggle>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Topic Analysis</h4>
              <span>Analyze story topics and themes</span>
            </SettingLabel>
            <SettingControl>
              <Toggle>
                <input 
                  type="checkbox" 
                  checked={settings.storyAnalysis.topicAnalysis}
                  onChange={(e) => handleSettingChange('storyAnalysis', 'topicAnalysis', e.target.checked)}
                />
                <span></span>
              </Toggle>
            </SettingControl>
          </SettingRow>
        </SettingCard>

        {/* Performance Metrics */}
        <SettingCard>
          <h3>📊 Performance Metrics</h3>
          <p>Current AI service performance and statistics</p>
          
          <SettingRow>
            <SettingLabel>
              <h4>Average Response Time</h4>
              <span>Time taken to analyze content</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status="online">{settings.performance.responseTime}s</StatusBadge>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Analysis Accuracy</h4>
              <span>Accuracy of content classification</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status="online">{settings.performance.accuracy}%</StatusBadge>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Service Uptime</h4>
              <span>Availability over the last 30 days</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status="online">{settings.performance.uptime}%</StatusBadge>
            </SettingControl>
          </SettingRow>
        </SettingCard>

        {/* Save Settings */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem' }}>
          {saveMessage && <span style={{ color: saveMessage.includes('✅') ? '#059669' : '#DC2626', fontWeight: 500 }}>{saveMessage}</span>}
          <TestButton onClick={handleTestConnection}>Test AI Connection</TestButton>
          <SaveButton onClick={handleSaveSettings} disabled={loading}>
            {loading ? 'Saving...' : 'Save Settings'}
          </SaveButton>
        </div>
      </SettingsGrid>
    </AISettingsContainer>
  );
};

export default AISettings;